import FormGroup from '~/components/form/FormGroup';
import { FormHorizontal } from '~/components/form/layout';
import ZodFieldInput from '~/components/zod/ZodFieldInput';
import { TenantOrderFormProps } from '~/tenants/common/form';
import { McmOrderContext, McmOrderType, McmPropertyType } from '~/tenants/mcm/model/McmOrder';
import ZodFieldSelect from '~/components/zod/ZodFieldSelect';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import ZodFieldTextarea from '~/components/zod/ZodFieldTextarea';
import { PersonaType } from '~/tenants/common/TenantPersona';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import Heading from '~/components/layout/Heading';
import Paragraph from '~/components/layout/Paragraph';
import Message from '~/components/Message';
import Sections, { Section } from '~/components/layout/Sections';
import ZodFieldCheckbox from '~/components/zod/ZodFieldCheckbox';

export default function McmOrderForm({ buyer, parent, persona }: TenantOrderFormProps<McmOrderContext>) {
  const crossroads = buyer.metadata.crossroads || parent?.crossroads;
  const type = useZodFormFieldSingleValue<McmOrderType>('type');

  return (
    <FormGroup>
      <FormHorizontal name="type" label="Order Type">
        <ZodFieldSelect
          name="type"
          options={[
            { value: McmOrderType.RESIDENTIAL, label: 'Residential' },
            { value: McmOrderType.RENTAL, label: 'Rental' },
          ]}
        />
      </FormHorizontal>
      {type !== McmOrderType.RENTAL && (
        <FormHorizontal name="property" label="Property Type">
          <ZodFieldSelect
            name="property"
            options={[
              { label: 'Single Family Home', value: McmPropertyType.SINGLE_FAMILY_HOME },
              { label: 'Condo', value: McmPropertyType.CONDO },
              { label: 'Townhome', value: McmPropertyType.TOWNHOME },
              { label: 'Mobile Home', value: McmPropertyType.MOBILE_HOME },
              { label: 'Multi Family', value: McmPropertyType.MULTI_FAMILY },
              { label: 'Land Only', value: McmPropertyType.LAND_ONLY },
            ]}
          />
        </FormHorizontal>
      )}
      <FormHorizontal name="sqft" label="Square Feet">
        <ZodFieldInput name="sqft" type="number" />
      </FormHorizontal>
      <FormHorizontal
        name="entry"
        label="Entry Instructions"
        description="Door codes, gate codes, etc. Who will meet the photographer?"
      >
        <ZodFieldInput name="entry" />
      </FormHorizontal>
      <FormHorizontal name="vacant" label="Vacancy">
        <ZodFieldSelect
          name="vacant"
          options={[
            { value: 'true', label: 'Unoccupied' },
            { value: 'false', label: 'Occupied' },
          ]}
        />
      </FormHorizontal>
      {crossroads && (
        <FormHorizontal name="price" label="Listing Price">
          <ZodFieldInput name="price" type="number" />
        </FormHorizontal>
      )}
      <FormHorizontal name="special" label="Special Instructions">
        <ZodFieldTextarea name="special" />
      </FormHorizontal>
      {persona === PersonaType.OFFICE ? (
        <ZodFieldHidden name="terms" value="true" />
      ) : (
        <Message type="info">
          <Sections>
            <Section>
              <Heading title="Cancellation Agreement" />
              <p>
                We understand that changes can happen and are sometimes needed. When a session is scheduled, one of our
                media specialists reserves that time specifically for your session and your client. If a cancellation
                happens at the last minute, we’re often unable to offer that timeslot to another client—or even reuse it
                for you if your plans shift again. That time often goes unused, even though a team member was prepared
                and scheduled specifically for your session.
              </p>
              <p>
                This cancellation agreement helps us keep our schedule running smoothly and efficiently, respect the
                time and commitment of our team, and continue delivering outstanding service at every session.
              </p>
              <ul>
                <li>
                  <strong>No Charge</strong>: Cancel anytime before 6:30 PM the day prior to your appointment—no
                  cancellation rate will be applied.
                </li>
                <li>
                  <strong>Same-Day Cancel</strong> (Within 2 Hours of Shoot Start): A 15% rate applies to all booked
                  services to help offset the time reserved for your session.
                </li>
                <li>
                  <strong>On-Site Cancel</strong> (Photographer En Route or Arrives to an Unshootable Home): A 15% rate
                  applies to all booked services to help offset the time reserved for your session.
                </li>
                <li>
                  <strong>Delayed Start</strong> (Home Not Ready Within 10 Minutes of Arrival): If the home isn’t ready
                  to shoot within 10 minutes of our arrival, we’ll do one of the following:
                  <ul className="pl-6 list-disc">
                    <li>Begin photographing the areas that are ready while finishing touches are made</li>
                    <li>
                      Proceed with photographing the home as is if it's not possible to complete preparations in time,
                      or
                    </li>
                    <li>Consider it an on-site cancel at the 50% rate if we’re unable to move forward.</li>
                  </ul>
                </li>
              </ul>
            </Section>

            <Section>
              <Heading title="Photo-Ready Reminder" />

              <p>
                Please visually verify that the home will be photo-ready prior to our arrival. This simple step helps
                ensure a smooth and efficient session—for you, your client, and our team.
              </p>
            </Section>

            <Section>
              <label className="flex items-center gap-2 mt-4 font-semibold cursor-pointer" htmlFor="terms">
                <ZodFieldCheckbox id="terms" name="terms" />
                I understand and agree to the terms of delays and cancellations.
              </label>
            </Section>
          </Sections>
        </Message>
      )}
    </FormGroup>
  );
}
